import * as Sentry from '@sentry/react'
import { env } from '@/config/env'

/**
 * Error tracking, performance tracing and session replay (sentry.udevs.io).
 * Off when VITE_SENTRY_DSN is empty (local development by default).
 *
 * Privacy: this console handles passwords, refresh tokens and customer data,
 * so HTTP bodies are never sent (login and user forms carry passwords,
 * /auth/refresh carries the refresh token), the user is identified by profile
 * id only, and replays keep the SDK defaults — every text masked, media blocked.
 */
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined

export const sentryEnabled = Boolean(dsn)

export function initSentry() {
  if (!dsn) return
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE === 'development' ? 'development' : env.appEnv,
    dataCollection: {
      userInfo: false,
      httpBodies: [],
    },
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing: every transaction.
    tracesSampleRate: 1.0,
    // Trace headers only on same-origin requests: the API host (api.myfidani.udevs.io)
    // does not allow sentry-trace/baggage in CORS, and adding them would fail every call.
    tracePropagationTargets: [/^\/(?!\/)/, window.location.origin],
    // Session Replay: 10% of sessions, every session with an error.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  })
}

/** Ties events to the signed-in profile (id only); null on sign-out. */
export function setSentryUser(profileId: string | null) {
  if (!dsn) return
  Sentry.setUser(profileId ? { id: profileId } : null)
}

/** React 19 root hooks: uncaught, caught (error boundaries, route errors) and recoverable errors. */
export const sentryRootOptions = dsn
  ? {
      onUncaughtError: Sentry.reactErrorHandler(),
      onCaughtError: Sentry.reactErrorHandler(),
      onRecoverableError: Sentry.reactErrorHandler(),
    }
  : undefined
